import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import FavoritesDrawer from "@/components/feature/FavoritesDrawer";
import { useFavorites } from "@/hooks/useFavorites";

const navItems = [
  { label: "首页", to: "/" },
  { label: "作品", to: "/works" },
  { label: "资讯", to: "/news" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { items } = useFavorites();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileOpen) return;
    const timer = window.setTimeout(() => mobileInputRef.current?.focus(), 120);
    return () => window.clearTimeout(timer);
  }, [mobileOpen]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-background-50 border-b border-background-200">
      <div className="container-content">
        <div className="flex items-center justify-between gap-3 h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-label font-extrabold text-xs md:text-sm tracking-tight">
              WM
            </span>
            <span className="flex flex-col">
              <span className="text-base md:text-lg font-heading font-bold text-foreground-950 leading-none tracking-wide">
                吴梦梦
              </span>
              <span className="mt-1 text-[9px] md:text-[10px] font-label tracking-[0.24em] text-foreground-500 leading-none">
                FILM PORTAL
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `relative px-3.5 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "text-accent-600 font-semibold"
                      : "text-foreground-700 hover:text-primary-600"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1.5 md:gap-2">
            <form onSubmit={submitSearch} className="hidden md:flex items-center">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm leading-none pointer-events-none" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索作品 / 资讯"
                  aria-label="搜索作品或资讯"
                  className="w-36 lg:w-52 h-9 pl-8 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:bg-background-50 transition-all"
                />
              </div>
              <button type="submit" className="sr-only">
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setMobileOpen((open) => !open)}
              aria-label="搜索"
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-search-line text-lg leading-none" />
            </button>

            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="打开我的收藏"
              className="relative w-9 h-9 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-bookmark-line text-lg leading-none" />
              {items.length > 0 && (
                <span className="absolute top-0 right-0 min-w-[16px] h-4 px-1 rounded-full bg-accent-500 text-primary-950 text-[10px] font-label font-bold flex items-center justify-center">
                  {items.length}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setMobileOpen((open) => !open)}
              aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
              aria-expanded={mobileOpen}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-2-line"} text-xl leading-none`} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-16 z-50">
          <button
            type="button"
            aria-label="关闭菜单背景"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 w-full h-full bg-primary-950/45 cursor-pointer"
          />
          <div className="relative bg-background-50 border-b border-background-200 px-4 pb-5 pt-4">
            <form onSubmit={submitSearch} className="flex items-center gap-2">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm leading-none" />
                <input
                  ref={mobileInputRef}
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索作品、资讯、演员动态"
                  aria-label="搜索作品或资讯"
                  className="w-full h-11 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:bg-background-50"
                />
              </div>
              <button
                type="submit"
                className="h-11 px-4 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <nav className="mt-4 flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-3 border-b border-background-200 text-[15px] ${
                      isActive ? "text-accent-600 font-semibold" : "text-foreground-800"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400 leading-none" />
                </NavLink>
              ))}
            </nav>

            <p className="mt-4 text-[11px] text-foreground-500 leading-relaxed">
              本站搜索为前端演示功能，未接入真实数据接口。
            </p>
          </div>
        </div>
      )}

      <FavoritesDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </header>
  );
}